/* Types and helpers shared by the mock route modules. */

import type { HttpMethod } from '../http'

export interface HandlerContext {
  params: Record<string, string>
  query: URLSearchParams
  body: unknown
}

export interface MockRoute {
  method: HttpMethod
  /** Express-style pattern, e.g. `/plants/:id`. */
  pattern: string
  handler: (context: HandlerContext) => unknown | Promise<unknown>
  /**
   * Response returned when the "empty" scenario is active, so empty states can
   * be reviewed deliberately instead of by luck.
   */
  emptyState?: unknown
}

export function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

export function readString(source: unknown, key: string): string | undefined {
  const value = asRecord(source)[key]
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined
}

export function readNumber(source: unknown, key: string): number | undefined {
  const value = asRecord(source)[key]
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

export function readBoolean(source: unknown, key: string): boolean | undefined {
  const value = asRecord(source)[key]
  return typeof value === 'boolean' ? value : undefined
}

/** Case-insensitive "does this text contain any of these terms" helper. */
export function matchesAny(haystack: string, needles: readonly string[]): boolean {
  const lower = haystack.toLowerCase()
  return needles.some((needle) => lower.includes(needle.toLowerCase()))
}

export function readQueryNumber(query: URLSearchParams, key: string): number | undefined {
  const raw = query.get(key)
  if (!raw) return undefined
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : undefined
}

export function paginate<T>(items: T[], page: number, pageSize: number): {
  items: T[]
  total: number
  page: number
  pageSize: number
} {
  const safePage = Math.max(1, page)
  const start = (safePage - 1) * pageSize
  return {
    items: items.slice(start, start + pageSize),
    total: items.length,
    page: safePage,
    pageSize,
  }
}
